"use strict";
// Copyright © Veeam Software Group GmbH
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PolicySchedule = void 0;
const react_1 = __importDefault(require("react"));
const core_1 = require("@veeam/core");
const components_1 = require("@veeam/components");
const PolicyScheduleInteractor_1 = require("./PolicyScheduleInteractor");
const core_2 = require("../../core");
const components_2 = require("./components");
require("./types");
const helpers_1 = require("./helpers");
const PolicySchedule = ({ isArchiveEnabled, isArchiveRepositoryExisting, texts, isRowThirdEnabled, thirdRowRepositoryName, archivedThirdRowRepositoryName, archivedThirdRowRepositoryStorageClass, dailyScheduleModel, weeklyScheduleModel, monthlyScheduleModel, yearlyScheduleModel, isDailyScheduleVisible = true, isWeeklyScheduleVisible = true, isMonthlyScheduleVisible = true, isYearlyScheduleVisible = true, isArchivable = true, dailyRetentionCountMin, monthlyViewAfterMainContent, yearlyViewAfterMainContent, dailyRetentionFieldsSettings: dailyRetentionFieldsSettingsFromProps, weeklyRetentionFieldsSettings: weeklyRetentionFieldsSettingsFromProps, monthlyRetentionFieldsSettings: monthlyRetentionFieldsSettingsFromProps, yearlyRetentionFieldsSettings: yearlyRetentionFieldsSettingsFromProps, }) => {
    const { t } = core_2.useFeatureContext();
    const dailyRetentionFieldsSettings = helpers_1.getDefaultRetentionFieldsSettings(dailyRetentionFieldsSettingsFromProps);
    const weeklyRetentionFieldsSettings = helpers_1.getDefaultRetentionFieldsSettings(weeklyRetentionFieldsSettingsFromProps);
    const monthlyRetentionFieldsSettings = helpers_1.getDefaultRetentionFieldsSettings(monthlyRetentionFieldsSettingsFromProps);
    const yearlyRetentionFieldsSettings = helpers_1.getYearlyDefaultRetentionFieldsSettings(yearlyRetentionFieldsSettingsFromProps);
    const { value: dailyScheduleModelValue } = core_1.useModel(dailyScheduleModel);
    const { value: weeklyScheduleModelValue } = core_1.useModel(weeklyScheduleModel);
    const { value: monthlyScheduleModelValue } = core_1.useModel(monthlyScheduleModel);
    const { value: yearlyScheduleModelValue } = core_1.useModel(yearlyScheduleModel);
    const dailySchedulePanelActions = components_2.useDailySchedulePanel({
        dailyScheduleModel,
        weeklyScheduleModel,
        monthlyScheduleModel,
        yearlyScheduleModel,
        retentionFieldsSettings: dailyRetentionFieldsSettings,
        texts: Object.assign(Object.assign({}, texts), texts.dailyScheduleControl),
    });
    const weeklySchedulePanelActions = components_2.useWeeklySchedulePanel({
        dailyScheduleModel,
        weeklyScheduleModel,
        monthlyScheduleModel,
        yearlyScheduleModel,
        dailyRetentionFieldsSettings,
        weeklyRetentionFieldsSettings,
        texts: Object.assign(Object.assign({}, texts), texts.weeklyScheduleControl),
    });
    const monthlySchedulePanelActions = components_2.useMonthlySchedulePanel({
        viewAfterMainContent: monthlyViewAfterMainContent,
        dailyScheduleModel,
        weeklyScheduleModel,
        monthlyScheduleModel,
        yearlyScheduleModel,
        texts: Object.assign(Object.assign({}, texts), texts.monthlyScheduleControl),
        isArchivable,
        retentionFieldsSettings: monthlyRetentionFieldsSettings,
    });
    const yearlySchedulePanelActions = components_2.useYearlySchedulePanel({
        viewAfterMainContent: yearlyViewAfterMainContent,
        dailyScheduleModel,
        weeklyScheduleModel,
        monthlyScheduleModel,
        yearlyScheduleModel,
        texts: Object.assign(Object.assign({}, texts), texts.yearlyScheduleControl),
        isArchivable,
        retentionFieldsSettings: yearlyRetentionFieldsSettings,
    });
    const { interactor } = core_1.useInteractor(() => {
        const scheduleTexts = Object.assign(Object.assign(Object.assign({ storageClass: texts.storageClass }, helpers_1.getScheduleInfoItemsCreatorTexts(t)), helpers_1.getYearlyScheduleInfoItemsCreatorTexts(t)), { excludedDays: t('scheduleInfo|form|labels|excludedDays'), excludedMonths: t('scheduleInfo|form|labels|excludedMonths') });
        const dailyWeeklyLabels = helpers_1.getScheduleInfoItemsCreatorLabels(texts.rowFirstLabel, texts.rowFirstTitleSingular, texts.rowFirstTitlePlural, texts.rowSecondLabel, texts.rowSecondTitleSingular, texts.rowSecondTitlePlural, texts.rowThirdLabel, texts.rowThirdTitleSingular, texts.rowThirdTitlePlural);
        const monthlyYearlyLabels = helpers_1.getArchivableScheduleInfoItemsCreatorLabels(isArchivable && monthlyScheduleModel.getValue().isArchiveEnabled, texts.rowFirstLabel, texts.rowFirstTitleSingular, texts.rowFirstTitlePlural, texts.rowSecondLabel, texts.rowSecondTitleSingular, texts.rowSecondTitlePlural, texts.rowThirdLabel, texts.rowArchivedThirdLabel, texts.rowThirdTitleSingular, texts.rowThirdTitlePlural);
        const panelActions = {
            dailySchedulePanelActions,
            weeklySchedulePanelActions,
            monthlySchedulePanelActions,
            yearlySchedulePanelActions,
        };
        const params = {
            isArchiveEnabled,
            isArchiveRepositoryExisting,
        };
        return new PolicyScheduleInteractor_1.PolicyScheduleInteractor(dailyScheduleModel, weeklyScheduleModel, monthlyScheduleModel, yearlyScheduleModel, params, scheduleTexts, dailyRetentionFieldsSettings, weeklyRetentionFieldsSettings, monthlyRetentionFieldsSettings, yearlyRetentionFieldsSettings, {
            dailySchedule: dailyWeeklyLabels,
            weeklySchedule: dailyWeeklyLabels,
            monthlySchedule: monthlyYearlyLabels,
            yearlySchedule: dailyWeeklyLabels,
        }, panelActions);
    });
    const isLoading = false;
    const isToggleDisabled = false;
    const commonScheduleInfoProps = {
        isLoading,
        isToggleDisabled,
        t,
    };
    const scheduleModels = {
        dailyScheduleModel,
        weeklyScheduleModel,
        monthlyScheduleModel,
        yearlyScheduleModel,
    };
    return (react_1.default.createElement(components_1.StackView, { direction: components_1.STACK_DIRECTION.column, gap: components_1.STACK_GAP.m, style: { width: '100%' } },
        dailyScheduleModelValue && isDailyScheduleVisible && (react_1.default.createElement(components_2.DailyScheduleInfo, Object.assign({}, commonScheduleInfoProps, scheduleModels, { dailyRetentionCountMin: dailyRetentionCountMin, scheduleInfoItems: interactor.getDailyScheduleInfoItems(thirdRowRepositoryName), scheduleEnabledField: interactor.dailyScheduleForm.fields.isEnabled, onShowPanel: interactor.handleShowDailyPanel }))),
        weeklyScheduleModelValue && isWeeklyScheduleVisible && (react_1.default.createElement(components_2.WeeklyScheduleInfo, Object.assign({}, commonScheduleInfoProps, scheduleModels, { scheduleInfoItems: interactor.getWeeklyScheduleInfoItems(thirdRowRepositoryName), scheduleEnabledField: interactor.weeklyScheduleForm.fields.isEnabled, onShowPanel: interactor.handleShowWeeklyPanel }))),
        monthlyScheduleModelValue && isMonthlyScheduleVisible && (react_1.default.createElement(components_2.MonthlyScheduleInfo, Object.assign({}, commonScheduleInfoProps, scheduleModels, { scheduleInfoItems: interactor.getMonthlyScheduleInfoItems(isArchivable, thirdRowRepositoryName, archivedThirdRowRepositoryName, archivedThirdRowRepositoryStorageClass), scheduleEnabledField: interactor.monthlyScheduleForm.fields.isEnabled, onShowPanel: interactor.handleShowMonthlyPanel }))),
        yearlyScheduleModelValue && isYearlyScheduleVisible && (react_1.default.createElement(components_2.YearlyScheduleInfo, Object.assign({}, commonScheduleInfoProps, scheduleModels, { scheduleInfoItems: interactor.getYearlyScheduleInfoItems(isRowThirdEnabled, isArchivable, isArchiveEnabled, thirdRowRepositoryName, archivedThirdRowRepositoryName, archivedThirdRowRepositoryStorageClass), scheduleEnabledField: interactor.yearlyScheduleForm.fields.isEnabled, isToggleDisabled: !isRowThirdEnabled, onShowPanel: interactor.handleShowYearlyPanel })))));
};
exports.PolicySchedule = PolicySchedule;
//# sourceMappingURL=PolicySchedule.js.map